import { useEffect } from 'react';
import {
  MESSAGE_TYPES,
  SIMULATION_TYPES,
  VAD_STATES,
  VOICE_MODE_STATUS
} from '../constants/simulationConstants';
import { isNudgytAgentCoreSession, postMessage } from '../helpers/utils';
import { useSimulationContext } from '../providers/SimulationProvider';

/**
 * Host -> iframe Live Chat control for Nudgyt Core.
 *
 * Core owns LIVE_CHAT; Classic owns VOICE_MODE ({@link useVoiceModeListener}),
 * so the two share no message type. Status frames keep the VAD_STATES
 * vocabulary the host already maps.
 */
function useCoreLiveChatListener() {
  const {
    isAiTalking,
    simulationType,
    characterEngineType,
    isClientInitialized,
    setIsLiveChatActive,
    setIsLiveChatInterrupt
  } = useSimulationContext();

  const isCoreBasicSession =
    isNudgytAgentCoreSession({ characterEngineType }) &&
    simulationType === SIMULATION_TYPES.BASIC;

  useEffect(() => {
    if (!isClientInitialized || !isCoreBasicSession) return;

    postMessage({
      type: MESSAGE_TYPES.LIVE_CHAT,
      payload: { status: VOICE_MODE_STATUS.INACTIVE }
    });
  }, [isClientInitialized, isCoreBasicSession]);

  useEffect(() => {
    if (!isClientInitialized || !isCoreBasicSession) return undefined;

    const handleMessage = (event) => {
      try {
        let receivedData = event.data;

        if (typeof receivedData === 'string') {
          try {
            receivedData = JSON.parse(receivedData);
          } catch {
            return;
          }
        }

        if (receivedData.type !== MESSAGE_TYPES.LIVE_CHAT) return;

        const { isEnable, isUserInterrupt } = receivedData.payload || {};

        if (typeof isEnable === 'boolean') {
          setIsLiveChatActive(isEnable);

          postMessage({
            type: MESSAGE_TYPES.LIVE_CHAT,
            payload: {
              status: isEnable
                ? VOICE_MODE_STATUS.ACTIVE
                : VOICE_MODE_STATUS.INACTIVE
            }
          });
        }

        // Tap-to-interrupt only applies while the character is speaking.
        if (isUserInterrupt === true && isAiTalking) {
          setIsLiveChatInterrupt(true);

          postMessage({
            type: MESSAGE_TYPES.LIVE_CHAT,
            payload: { status: VAD_STATES.IDLE }
          });
        }
      } catch (error) {
        console.error('[Core Live Chat Listener Error]:', error);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [
    isAiTalking,
    isCoreBasicSession,
    isClientInitialized,
    setIsLiveChatActive,
    setIsLiveChatInterrupt
  ]);
}

export default useCoreLiveChatListener;
